import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY;

/**
 * Wrapper around Cloudflare Turnstile that exposes a `reset()` imperative
 * handle so caller forms can wipe the token after a failed submit.
 *
 * Renders nothing if the sitekey isn't configured (graceful degradation).
 *
 * Props
 * -----
 * - onVerify: (token: string) => void — called when widget produces a token.
 * - onExpire: () => void — called when token expires.
 * - theme: 'light' | 'dark' | 'auto'
 * - size: 'normal' | 'compact' | 'invisible'
 * - testId: data-testid for the wrapper.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onVerify, onExpire, theme = 'light', size = 'normal', testId = 'turnstile-widget' },
  ref,
) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    reset() { try { inner.current?.reset(); } catch { /* ignored */ } },
  }), []);

  if (!SITEKEY) return null;

  return (
    <div data-testid={testId} className="my-2">
      <Turnstile
        ref={inner}
        siteKey={SITEKEY}
        options={{ theme, size, action: testId }}
        onSuccess={(t) => onVerify?.(t)}
        onExpire={() => onExpire?.()}
        onError={() => onExpire?.()}
      />
    </div>
  );
});

export default TurnstileWidget;
export const turnstileConfigured = !!SITEKEY;
